import { Injectable, signal, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApiService } from 'src/app/shared/services/api.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';

@Injectable({
   providedIn: 'root',
})
export class SignupStateService {
   private platformId = inject(PLATFORM_ID);
   private isBrowser = isPlatformBrowser(this.platformId);
   private apiService = inject(ApiService);
   private authService = inject(AuthService);
   private subscriptionService = inject(SubscriptionService);

   private _email = signal<string>('');
   private _password = signal<string>('');
   private _firstName = signal<string>('');
   private _lastName = signal<string>('');
   private _phone = signal<string>('');
   private _rawPhone = signal<string>('');
   private _countryCode = signal<string>('');
   
   // Onboarding Config Flags
   private _enableFreeTrial = signal<number>(0);
   private _activationStep = signal<'user-info' | 'verify-phone' | 'onboarding-options'>('user-info');

   readonly email = this._email.asReadonly();
   readonly password = this._password.asReadonly();
   readonly firstName = this._firstName.asReadonly();
   readonly lastName = this._lastName.asReadonly();
   readonly phone = this._phone.asReadonly();
   readonly rawPhone = this._rawPhone.asReadonly();
   readonly countryCode = this._countryCode.asReadonly();
   readonly enableFreeTrial = this._enableFreeTrial.asReadonly();
   readonly activationStep = this._activationStep.asReadonly();

   constructor() {
      if (this.isBrowser) {
         // Email survives refresh for UX (Smart Resumability)
         this._email.set(sessionStorage.getItem('temp_signup_email') || '');
         
         // Persist onboarding config for consistent UI on refresh
         const storedFreeTrial = sessionStorage.getItem('temp_onboarding_free_trial');
         if (storedFreeTrial !== null) {
            this._enableFreeTrial.set(Number(storedFreeTrial));
         }

         // Password NEVER touches storage for security
         this._password.set('');
      }
   }

   setSignupData(email: string, password: string) {
      this._email.set(email);
      this._password.set(password);
      if (this.isBrowser) {
         sessionStorage.setItem('temp_signup_email', email);
      }
   }

   setUserInfo(firstName: string, lastName: string, phone: string, rawPhone: string = '', countryCode: string = '') {
      this._firstName.set(firstName);
      this._lastName.set(lastName);
      this._phone.set(phone);
      this._rawPhone.set(rawPhone);
      this._countryCode.set(countryCode);
   }

   setOnboardingConfig(freeTrial: number) {
      this._enableFreeTrial.set(freeTrial);
      if (this.isBrowser) {
         sessionStorage.setItem('temp_onboarding_free_trial', String(freeTrial));
      }
   }

   setActivationStep(step: 'user-info' | 'verify-phone' | 'onboarding-options') {
      this._activationStep.set(step);
   }

   clear() {
      this._email.set('');
      this._password.set('');
      this._firstName.set('');
      this._lastName.set('');
      this._phone.set('');
      this._rawPhone.set('');
      this._countryCode.set('');
      this._enableFreeTrial.set(0);
      if (this.isBrowser) {
         sessionStorage.removeItem('temp_signup_email');
         sessionStorage.removeItem('temp_onboarding_free_trial');
      }
   }

   async claimPremiumAccess() {
      // Only claim premium if a trial is offered (enableFreeTrial > 0)
      if (this.enableFreeTrial() === 0) {
         return;
      }

      try {
         let response: any = await this.apiService.claimPremiumAccess();

         if (response.status_code === 'ALREADY_ACTIVATED' || response.status_code === 'ALREADY_CLAIMED') {
            this.authService.setOnboardingTrialClaimed(true);
            await this.subscriptionService.setUserSubscriptionInfo();
            return;
         }

         // Poll until claim is confirmed
         let shouldPoll = true;
         while (shouldPoll) {
            response = await this.apiService.claimPremiumAccess();
            if (response.status_code === 'ALREADY_CLAIMED') {
               shouldPoll = false;
               this.authService.setOnboardingTrialClaimed(true);
               await this.subscriptionService.setUserSubscriptionInfo();
            } else {
               await new Promise(resolve => setTimeout(resolve, 3000));
            }
         }
      } catch (error) {
         console.error('Error claiming premium access:', error);
         // Don't block the user flow on claim failure
      }
   }
}
